import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useRef, useState } from "react";

import { ErrorBanner } from "@/components/Callout";
import { Page } from "@/components/Page";
import { Button, ButtonAnchor } from "@/components/ui/button";
import {
  CountryDropdown,
  DEFAULT_COUNTRY,
} from "@/components/ui/country-select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/components/ui/select";
import { H1 } from "@/components/ui/typography";
import { CreateOptions, createStripeAccount } from "@/lib/api";

export const Route = createFileRoute("/atprotofans/connect-stripe")({
  component: HomeComponent,
  head: () => ({ meta: [{ title: "Setup Monetization" }] }),
});

function HomeComponent() {
  const formRef = useRef<HTMLFormElement>(null);
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [stripeUrl, setStripeUrl] = useState<string | null>(null);
  const mutation = useMutation({
    onError: (error: AxiosError) => console.error(error),
    mutationFn: async (form: FormData) => {
      const options: CreateOptions = {
        email: form.get("email") as string,
        business_type: form.get("business-type") as "individual" | "company",
        individual_first_name: form.get("first-name") as string | undefined,
        individual_last_name: form.get("last-name") as string | undefined,
        company_name: form.get("company") as string | undefined,
        country: country.alpha2,
      };

      if (
        !options.company_name &&
        !(options.individual_first_name && options.individual_last_name)
      ) {
        throw new AxiosError(
          "You must enter either a company name or first and last name.",
        );
      }

      const result = await createStripeAccount(options);

      setStripeUrl(result.account_links.url);
    },
  });

  if (stripeUrl) {
    return (
      <div className="max-w-[500px] w-full mx-auto flex flex-col gap-8 items-center text-center">
        <H1 className="text-5xl md:text-8xl">Success</H1>

        <p className="text-lg">
          You've completed the first step in the setting up payouts on your
          ATProtoFans account. Now you just need to complete the steps in the
          Stripe account setup process.
        </p>

        <div className="flex flex-col gap-4">
          <ButtonAnchor href={stripeUrl}>
            Complete Stripe Account Setup
          </ButtonAnchor>
        </div>
      </div>
    );
  }

  return (
    <Page>
      <div className="max-w-[500px] w-full mx-auto flex flex-col gap-8 bg-slate-subtle p-8 border border-slate-7 dark:border-slatedark-7 rounded-lg">
        <div className="flex gap-4 justify-between">
          <H1>Setup Monetization</H1>
        </div>

        <p>
          To setup payouts on your ATProtoFans account, we need to collect some
          basic information about you.
        </p>

        {mutation.error && <ErrorBanner error={mutation.error} />}

        <form
          ref={formRef}
          className="flex flex-col gap-6"
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target as HTMLFormElement);
            mutation.mutate(formData);
          }}
        >
          <div className="flex gap-4">
            <div className="flex-1 flex flex-col gap-4">
              <Label htmlFor="first-name">First Name</Label>
              <Input id="first-name" name="first-name" />
            </div>
            <div className="flex-1 flex flex-col gap-4">
              <Label htmlFor="last-name">Last Name</Label>
              <Input id="last-name" name="last-name" />
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <Label htmlFor="email">Email</Label>
            <Input ref={emailInputRef} required id="email" name="email" />
          </div>
          <div className="flex gap-4">
            <div className="flex-1 flex flex-col gap-4">
              <Label htmlFor="business-type">Business Type</Label>
              <SimpleSelect
                aria-label="Business Type"
                id="business-type"
                name="business-type"
                defaultValue="individual"
                options={[
                  { label: "Individual", value: "individual" },
                  { label: "Company", value: "company" },
                ]}
              />
            </div>
            <div className="flex-1 flex flex-col gap-4">
              <Label htmlFor="company">Company</Label>
              <Input id="company" name="company" />
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-1 flex flex-col gap-4">
              <Label htmlFor="country">Country</Label>
              <CountryDropdown
                placeholder="Select country"
                defaultValue={country.alpha3}
                onChange={setCountry}
              />
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <Button type="submit" isLoading={mutation.isPending}>
              Submit
            </Button>
          </div>
        </form>
      </div>
    </Page>
  );
}
